import React, { useEffect, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import PageBreadcrumb from '../../components/common/PageBreadCrumb';
import ComponentCard from '../../components/common/ComponentCard';
import PageMeta from '../../components/common/PageMeta';
import { Button, Label, Modal, TextInput } from 'flowbite-react';
import { HiOutlineExclamationCircle } from 'react-icons/hi';
import toast from 'react-hot-toast';
import { useCollegeStore } from '../../store/useCollegeStore';
import { useAboutStore } from '../../store/useAboutStore';
import { useCanAccessResource, useHasPermission, useIsMainAdmin } from '../../utils/permissions';
import { usePermissionStore } from '../../store/usePermissionStore';
import AccessDenied from '../../components/common/AccessDenied';

export default function AboutPage() {
  const getAbout = useAboutStore((state) => state.getAbout);
  const updateAbout = useAboutStore((state) => state.updateAbout);
  const deleteAbout = useAboutStore((state) => state.deleteAbout);
  const about = useAboutStore((state) => state.about);
  const loading = useAboutStore((state) => state.loading);
  const uploadFile = useCollegeStore((state) => state.uploadFile);
  const uploadedFile = useCollegeStore((state) => state.uploadedFile);

  const canAccess = useCanAccessResource('about');
  const hasReadPermission = useHasPermission('about', 'read');
  const hasUpdatePermission = useHasPermission('about', 'update');
  const hasDeletePermission = useHasPermission('about', 'delete');
  const isMainAdmin = useIsMainAdmin();
  const isLoadingPermissions = usePermissionStore((state) => state.isLoading);
  const hasFetched = usePermissionStore((state) => state.hasFetched);

  const [openModal, setOpenModal] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [image, setImage] = useState('');
  const [isUploading, setIsUploading] = useState<boolean>(false);

  // Quill editor modules
  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ align: [] }],
      ['link', 'image'],
      ['clean'],
    ],
  };

  const quillFormats = ['header', 'bold', 'italic', 'underline', 'strike', 'list', 'bullet', 'align', 'link', 'image'];

  useEffect(() => {
    if (hasFetched && !isLoadingPermissions && (hasReadPermission || isMainAdmin)) {
      getAbout();
    }
  }, [getAbout, hasReadPermission, isMainAdmin, hasFetched, isLoadingPermissions]);

  useEffect(() => {
    if (about) {
      setTitle(about.title || 'About Us');
      setContent(about.content || '');
      setImage(about.image || '');
    }
  }, [about]);

  const handleSave = async () => {
    if (!content || content.trim() === '') {
      toast.error('Please provide content for the about page.', {
        duration: 2000,
        icon: '❌',
      });
      return;
    }

    await updateAbout({
      title: title || 'About Us',
      content,
      image: image || '',
    });
  };

  const handleDelete = () => {
    deleteAbout();
    setOpenModal(false);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      // Check file size (5MB limit)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (file.size > maxSize) {
        toast.error('File size exceeds the maximum limit of 5MB. Please upload a smaller file.', {
          duration: 3000,
          icon: '❌',
        });
        e.target.value = ''; // Reset file input
        return;
      }

      setIsUploading(true);
      try {
        await uploadFile(file);
      } catch (error) {
        // Error will be handled by displayErrorToast in the API function
      } finally {
        setIsUploading(false);
      }
    }
  };

  useEffect(() => {
    if (uploadedFile?.data?.upload) {
      const upload = uploadedFile.data.upload as any;
      // Handle different response formats (ImageKit returns url, S3 returns Location, old format has uploadUrl)
      if (upload.url) {
        setImage(upload.url);
      } else if (upload.Location) {
        setImage(upload.Location);
      } else if (upload.uploadUrl) {
        setImage(upload.uploadUrl);
      }
    }
  }, [uploadedFile]);

  if (!hasFetched || isLoadingPermissions) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#B8860B] mx-auto"></div>
          <p className="mt-4 text-gray-600" style={{ fontFamily: 'Lato, sans-serif' }}>
            Loading...
          </p>
        </div>
      </div>
    );
  }

  if (!canAccess && !isMainAdmin) {
    return <AccessDenied resourceName="About Page" />;
  }

  if (!hasReadPermission && !isMainAdmin) {
    return <AccessDenied resourceName="About Page" />;
  }

  return (
    <>
      <Modal
        show={openModal}
        size="md"
        position="center"
        onClose={() => {
          setOpenModal(false);
        }}
        popup
      >
        <Modal.Header />
        <Modal.Body>
          <div className="text-center">
            <HiOutlineExclamationCircle className="mx-auto mb-4 h-14 w-14 text-gray-400 dark:text-gray-200" />
            <h3 className="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400" style={{ fontFamily: 'Lato, sans-serif' }}>
              Are you sure you want to delete the about page?
            </h3>
            <div className="flex justify-center gap-4">
              <Button color="failure" onClick={handleDelete}>
                Yes, I'm sure
              </Button>
              <Button color="gray" onClick={() => setOpenModal(false)}>
                No, cancel
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <PageMeta title="About Page - Church Management System" description="Manage the about page content with rich text editor" />
      <PageBreadcrumb pageTitle="About Page" />

      <ComponentCard title="About Page Management">
        <div className="space-y-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Manage About Page
            </h2>
            <p className="text-gray-600" style={{ fontFamily: 'Lato, sans-serif' }}>
              Edit the about page content using the rich text editor below. This content will be displayed on the public about page.
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="title" value="Title (Optional)" className="mb-2" style={{ fontFamily: 'Lato, sans-serif' }} />
              <TextInput
                id="title"
                type="text"
                placeholder="About Us"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                style={{ fontFamily: 'Lato, sans-serif' }}
              />
            </div>

            <div>
              <Label htmlFor="image" value="Image (Optional)" className="mb-2" style={{ fontFamily: 'Lato, sans-serif' }} />
              <input
                type="file"
                id="image"
                accept="image/*"
                onChange={handleImageUpload}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#B8860B] file:text-white hover:file:bg-[#9A7209] cursor-pointer"
                style={{ fontFamily: 'Lato, sans-serif' }}
                disabled={isUploading}
              />
              {isUploading && (
                <p className="mt-2 text-sm text-gray-600" style={{ fontFamily: 'Lato, sans-serif' }}>
                  Uploading...
                </p>
              )}
              {image && (
                <div className="mt-4">
                  <img src={image} alt="Preview" className="max-w-md h-auto rounded-lg shadow-md" />
                  <Button color="failure" size="sm" className="mt-2" onClick={() => setImage('')}>
                    Remove Image
                  </Button>
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="content" value="Content *" className="mb-2" style={{ fontFamily: 'Lato, sans-serif' }} />
              <div className="mt-2">
                <ReactQuill
                  theme="snow"
                  value={content}
                  onChange={setContent}
                  modules={quillModules}
                  formats={quillFormats}
                  style={{ minHeight: '400px' }}
                />
              </div>
            </div>

            <div className="flex gap-4 pt-4">
              <Button
                onClick={handleSave}
                disabled={loading || !content || content.trim() === ''}
                className="bg-[#B8860B] hover:bg-[#9A7209]"
                style={{ fontFamily: 'Lato, sans-serif' }}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
              {(hasDeletePermission || isMainAdmin) && (
                <Button color="failure" onClick={() => setOpenModal(true)} disabled={loading || !about} style={{ fontFamily: 'Lato, sans-serif' }}>
                  Delete About Page
                </Button>
              )}
            </div>
          </div>
        </div>
      </ComponentCard>
    </>
  );
}
